import role13MfrsSchema from '../../../../assets/forms/role-310100-mfrs.form.json';
import role13MpersSchema from '../../../../assets/forms/role-310100-mpers.form.json';

/**
 * Shared business rules for Role 13 (Analysis of Profit or Loss).
 *
 * Role 13 is an accounting-standard dependent sheet: the same logical sheet
 * ships as two sibling form JSONs — role-310100-mfrs.form.json and
 * role-310100-mpers.form.json — that differ in qname prefixes (ifrs-full:* /
 * ssmt-mfrs:* vs ifrs-smes:* / ssmt-mpers:*). Mirrors role 20's pattern.
 *
 * Both role13-310100-analysis-of-profit-or-loss.component.ts AND
 * role13AnalysisOfProfitOrLossValidator import from this file so the schema
 * selection literal and shared types live in exactly one place. If the
 * backend ever renames the accounting-standard enum, only this file changes —
 * the parity-check harness will catch any consumer that drifted.
 *
 * ---- Selection semantics (important, differs from role 20) --------------
 *
 * The role 13 component resolves its schema via
 *   this.crossRefService.isMFRS()
 *     ? 'role-310100-mfrs.form.json'
 *     : 'role-310100-mpers.form.json'
 *
 * `CrossReferenceService.isMFRS()` returns true ONLY when the filing's
 * accounting standard is the exact literal
 *   'Malaysian Financial Reporting Standards'
 * Any other value (including undefined, '', or 'Malaysian Private Entity
 * Reporting Standards') falls through to MPERS. This is the OPPOSITE default
 * from role 20 (which treats MFRS as the default), so the picker below
 * intentionally inverts the role 20 style: MFRS requires exact match, MPERS
 * is the fallback. Do not "normalise" it without auditing the component.
 */

/**
 * Literal value of
 * filingPrerequisites['ssmt-dei:BasisOfAccountingStandardsAppliedToPrepareFinancialStatements']
 * that selects the MFRS schema. See CrossReferenceService.isMFRS() for the
 * source of truth.
 */
export const ROLE13_MFRS_ACCOUNTING_STANDARD =
  'Malaysian Financial Reporting Standards';

/** Filename of the MFRS variant schema. */
export const ROLE13_MFRS_SCHEMA_FILE = 'role-310100-mfrs.form.json';

/** Filename of the MPERS (default / fallback) variant schema. */
export const ROLE13_MPERS_SCHEMA_FILE = 'role-310100-mpers.form.json';

/**
 * Narrow shape of a role 13 schema control used by the pure validator.
 *
 * The schemas carry richer metadata (ui.level, linkTo, calculated, readonly,
 * currency, placeholder, etc.) that drives the editor's rendering but does
 * not influence sheet-level validation. The validator only needs qname,
 * type, required, and validators — the same information any future
 * required-field fix-guard would consume.
 */
/**
 * A schema-declared equality rule on a role 13 control. Today the only one is
 * the MPERS-only `(Reversal of)/Impairment loss on inventories`
 * (`ifrs-smes:ReversalOfInventoryWritedown`) which must tie to the same field
 * on Role 18 SOCF (the reciprocal of the rule role 18 already enforces against
 * role 13). Shape mirrors Role18SchemaEqualityRule so the two validators read
 * equality identically.
 */
export interface Role13SchemaEqualityRule {
  sheet: string;
  field: string;
  taxonomy?: string;
  operation?: 'sum';
  skipIfSourceNull?: boolean;
  skipIfTargetNull?: boolean;
  message?: string;
}

export interface Role13SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Array<{ name?: string; conditionalOn?: unknown }>;
  validation?: {
    equality?: Role13SchemaEqualityRule[];
  };
  ui?: {
    controlType?: string;
    dualColumn?: boolean;
    calculated?: boolean;
    readonly?: boolean;
    level?: number | string;
  };
}

export interface Role13Schema {
  role: string;
  sheetId: string;
  label: string;
  description?: string;
  controls: Role13SchemaControl[];
}

/**
 * Resolve the correct role 13 schema filename for the given accounting
 * standard. Mirrors CrossReferenceService.isMFRS() so the component's
 * fetch() and the validator's static import both derive from the same rule.
 */
export function pickRole13SchemaFile(
  accountingStandard: string | undefined | null
): string {
  if (accountingStandard === ROLE13_MFRS_ACCOUNTING_STANDARD) {
    return ROLE13_MFRS_SCHEMA_FILE;
  }
  return ROLE13_MPERS_SCHEMA_FILE;
}

/**
 * Resolve the actual role 13 schema object for the given accounting
 * standard. Used by the pure validator which cannot fetch() at validate-
 * time (the listing/dashboard cache-miss path runs synchronously) so both
 * variants are imported eagerly and selected from memory.
 */
export function pickRole13Schema(
  accountingStandard: string | undefined | null
): Role13Schema {
  if (accountingStandard === ROLE13_MFRS_ACCOUNTING_STANDARD) {
    return role13MfrsSchema as unknown as Role13Schema;
  }
  return role13MpersSchema as unknown as Role13Schema;
}

/**
 * Schema controls that participate in validation. Excludes section headers
 * exactly as the component's buildForm() does (controlType === 'section-header'
 * is the editor's skip-list). Section headers never become FormControls and
 * therefore cannot fail validation.
 */
export function getRole13ValidatingControls(
  schema: Role13Schema
): ReadonlyArray<Role13SchemaControl> {
  return schema.controls.filter(
    (control) => control.type !== 'SectionHeader' && control.ui?.controlType !== 'section-header'
  );
}

/**
 * Is this control a calculated / readonly total? Calculated fields are
 * populated by CalculationService.calculateTotals_310100 at runtime and are
 * never user-editable, so even if a future schema edit marked one required,
 * the validator should not flag emptiness until the calculation engine has
 * run. Mirrors the component's `calculated === true || readonly === true`
 * filter in filterOutCalculatedFields().
 */
export function isRole13CalculatedControl(
  control: Role13SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/**
 * Map the filing's accounting standard onto the taxonomy label the equality
 * comparison uses. Same rule as pickRole13Schema (MFRS requires the exact
 * literal; everything else is MPERS) so the selected schema and the taxonomy
 * gate never disagree.
 */
export function role13AccountingStandardToTaxonomy(
  accountingStandard: string | undefined | null
): 'MFRS' | 'MPERS' {
  return accountingStandard === ROLE13_MFRS_ACCOUNTING_STANDARD ? 'MFRS' : 'MPERS';
}

/**
 * Flat form-control key for a role 13 dualColumn cell — mirrors the component's
 * `${qname}[${cons}][${period}]` (getXbrlControlName) and the role 18 / role 8
 * equality readers, so the headless equality compare reads the same keys the
 * editor wrote.
 */
export function buildRole13ColumnKey(
  qname: string,
  dimension: 'Consolidated' | 'Separate',
  period: 'CurrentYear' | 'PreviousYear'
): string {
  return `${qname}[${dimension}][${period}]`;
}

/** A control with no monetary value to compare (null/undefined/''). */
export function isRole13Empty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}
